// @flow
import React, { type Node, Component } from 'react';

import api from 'api';

import Alert from 'bs/Alert';
import PageSpinner from 'components/PageSpinner';
import { type Callbacks } from 'types/api';
import { AbortController } from 'utils/apiHelpers';

type FetcherFunction = (callbacks: Callbacks) => void;

type FetchRenderProps = {
    /** Manual fetch function, for triggering fetches inside rendered components. */
    fetch: FetcherFunction,
};

type FetcherProps = {
    /** Initial fetch function, called on mount */
    fetch?: (callbacks: Callbacks) => void,
    /** Rendered on successful fetch */
    render(fetchProps: FetchRenderProps): Node,
    /** Renders error. Defaults to bs/Alert */
    renderError?: (e: Error) => void,
    /** Renders while fetching. Defaults to PageSpinner */
    renderFetching?: () => void,
    /** Custom callbacks that will be chained after Fetcher's callbacks */
    callbacks: ?Callbacks,
};

type FetcherState = {
    fetching: boolean,
    error: null | Error,
};

class Fetcher extends Component<FetcherProps, FetcherState> {
    _isMounted = false;

    state = {
        fetching: true,
        responseData: null,
        error: null,
    };

    static defaultProps = {
        callbacks: {},
    };

    constructor(props: FetcherProps) {
        super(props);
        this.controller = new AbortController();
        this.fetch = this.fetch.bind(this);
    }

    fetch(fetcher?: FetcherFunction) {
        const callbacks = this.getCallbacks();
        fetcher = fetcher || this.props.fetch;
        fetcher(callbacks);
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.fetch) {
            this.fetch();
        } else {
            this.setState({ fetching: false });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.controller.abort();
    }

    getCallbacks() {
        const passedCallbacks = this.props.callbacks;
        const start = () => {
            this.setState({ fetching: true, error: null });
            if (passedCallbacks.start) {
                passedCallbacks.start();
            }
        };
        const done = response => {
            if (this._isMounted) {
                this.setState({ fetching: false, error: null, responseData: response });
            }
            if (passedCallbacks.done) {
                passedCallbacks.done(response);
            }
        };
        const fail = (errorText, data = {}) => {
            const error = data.error || errorText;
            if (this._isMounted) {
                this.setState({ fetching: false, error });
            }
            if (passedCallbacks.fail) {
                passedCallbacks.fail(error);
            }
        };
        const signal = this.controller.signal;

        return { start, done, fail, signal };
    }

    renderError() {
        if (this.props.renderError) {
            return this.props.renderError(this.state.error);
        } else {
            return <Alert>There was an error.</Alert>;
        }
    }

    renderFetching() {
        if (this.props.renderFetching) {
            return this.props.renderFetching();
        } else {
            return <PageSpinner />;
        }
    }

    render() {
        if (this.state.fetching) {
            return this.renderFetching();
        } else if (this.state.error) {
            return this.renderError();
        } else {
            return this.props.render({
                fetch: this.fetch,
                responseData: this.state.responseData,
            });
        }
    }
}

export const SimpleFetcher = ({ url, method = 'get', ...fetcherProps }) => (
    // Shortcut to just use the default start/done/fail callbacks
    // which is what we end up doing much of the time. Avoids
    // having to write boilerplate api promise chains, and
    // anytime I can avoid writing promises I will.
    //
    // Could probably just be clever about modifying Fetcher
    // to accept url/method instead of full fetch function
    // but to keep it simple I'm just making it a separate thing
    <Fetcher
        fetch={({ start, done, fail }) => {
            start();
            api[method](url).then(done).catch(fail);
        }}
        {...fetcherProps}
    />
);

export default Fetcher;
